import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/data/DataTable";
import { Badge } from "@/components/ui/badge";
import { RelTime } from "@/components/data/RelTime";
import { JsonViewer } from "@/components/data/JsonViewer";
import { useAudit, paginatedItems } from "@/lib/queries";
import type { AuditRow } from "@/types";

const PAGE = 50;

function parseDetails(d: AuditRow["details"]) {
  if (d == null) return null;
  if (typeof d === "string") {
    try {
      return JSON.parse(d);
    } catch {
      return d;
    }
  }
  return d;
}

export function AuditPage() {
  const [page, setPage] = useState(0);
  const q = useAudit({ limit: PAGE, offset: page * PAGE });
  const rows = paginatedItems<AuditRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<AuditRow>[] = [
    { key: "time", header: "Time", cell: (r) => <span className="text-xs text-muted-foreground"><RelTime value={r.created_at} /></span>, width: 130 },
    {
      key: "admin",
      header: "Admin",
      cell: (r) => <span className="font-mono text-xs">{r.admin_email ?? r.admin_user_id.slice(0, 8)}</span>,
      width: 220,
    },
    {
      key: "action",
      header: "Action",
      cell: (r) => <Badge variant={r.action.includes("delete") ? "error" : r.action.includes("ban") ? "warn" : "info"} className="font-mono">{r.action}</Badge>,
      width: 180,
    },
    {
      key: "target",
      header: "Target",
      cell: (r) =>
        r.target_user_id ? (
          <Link to={`/users/${r.target_user_id}`} className="font-mono text-xs text-sky-300 hover:underline">
            {r.target_email ?? r.target_user_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    { key: "ip", header: "IP", cell: (r) => <span className="font-mono text-xs text-muted-foreground">{r.ip ?? "—"}</span>, width: 130 },
  ];

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Actions taken through /api/admin (reset data or onboarding, delete user, send push, template and feedback edits, resyncs). Ban, unban, set password, create user and session revokes go through better-auth and aren't recorded here yet."
      />
      <DataTable
        columns={cols}
        rows={rows}
        loading={q.isLoading}
        error={q.isError ? q.error : undefined}
        onRetry={() => q.refetch()}
        rowKey={(r) => r.id}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE}
        totalRows={total}
        expandable={(r) => {
          const d = parseDetails(r.details);
          if (d == null) return <span className="text-xs text-muted-foreground">No additional details.</span>;
          if (typeof d === "string") return <pre className="font-mono text-xs">{d}</pre>;
          return <JsonViewer value={d} />;
        }}
      />
    </>
  );
}
